import React, { FC, useRef } from 'react';

import classNames from 'classnames';

import { PdfViewerClient } from '../client/pdfViewerClient';
import {
    SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, SIDEBAR_WIDTH, THUMBNAIL_MARGIN,
} from '../constants';
import useDragResize from '../hooks/useDragResize';
import useOutline from '../hooks/useOutline';
import useSharedObserver from '../hooks/useSharedObserver';
import { PageInfo } from '../types';
import Outline from './Outline';
import Thumbnail from './Thumbnail';
import { SidebarView } from './Toolbar';
import styles from '../PdfTileViewer.module.scss';

interface SidebarProps {
    client: PdfViewerClient;
    pages: PageInfo[];
    current: number;
    isOpen: boolean;
    view: SidebarView;
    onSelect: (index: number) => void;
    onClose: () => void;
}

const Sidebar: FC<SidebarProps> = ({
    client, pages, current, isOpen, view, onSelect, onClose,
}) => {
    const sidebarRef = useRef<HTMLDivElement>(null);
    const observe = useSharedObserver(sidebarRef, THUMBNAIL_MARGIN);
    const { width, onMouseDown } = useDragResize(SIDEBAR_WIDTH, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH);
    const outline = useOutline(client, isOpen && view === 'outline');

    return (
        <>
            <div
                className={classNames(styles.backdrop, { [styles.hidden]: !isOpen })}
                role="presentation"
                onClick={onClose}
            />
            <div
                ref={sidebarRef}
                className={classNames(styles.sidebar, { [styles.hidden]: !isOpen })}
                style={{ width }}
            >
                <div className={classNames(styles.thumbnails, { [styles.hidden]: view !== 'thumbnails' })}>
                    {pages.map((page, index) => (
                        <Thumbnail
                            key={page.page}
                            index={index}
                            page={page}
                            selected={index === current}
                            isPaneActive={isOpen && view === 'thumbnails'}
                            client={client}
                            observe={observe}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
                <div className={classNames(styles.outline, { [styles.hidden]: view !== 'outline' })}>
                    <Outline state={outline} onSelect={onSelect} />
                </div>
            </div>
            <div
                className={classNames(styles.resizer, { [styles.hidden]: !isOpen })}
                role="presentation"
                onMouseDown={onMouseDown}
            />
        </>
    );
};

export default Sidebar;
